import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import Account from '@jwp/ott-theme/assets/icons/account.svg?react';
import SettingsIcon from '@jwp/ott-theme/assets/icons/Settings.svg?react';
import BalanceWallet from '@jwp/ott-theme/assets/icons/balance_wallet.svg?react';
import VideoIcon from '@jwp/ott-theme/assets/icons/reel.svg?react';
import ModerateIcon from '@jwp/ott-theme/assets/icons/moderate.svg?react';
import Exit from '@jwp/ott-theme/assets/icons/exit.svg?react';
import { userProfileURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { PATH_USER_ACCOUNT, PATH_USER_PAYMENTS, PATH_USER_VIDEOS, PATH_USER_SETTINGS, PATH_USER_MODERATE } from '@jwp/ott-common/src/paths';
import type { Profile } from '@jwp/ott-common/types/profiles';
import { cognitoLogout } from '@jwp/ott-common/src/services/integrations/CustomAccountService';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { ROLES } from '@jwp/ott-common/src/constants';
import useBreakpoint, { Breakpoint } from '../../hooks/useBreakpoint';

import MenuButton from '../MenuButton/MenuButton';
import Icon from '../Icon/Icon';
import ProfileCircle from '../ProfileCircle/ProfileCircle';
import ConfirmationDialog from '../ConfirmationDialog/ConfirmationDialog';

import styles from './UserMenu.module.scss';

type Props = {
  small?: boolean;
  focusable: boolean;
  showPaymentsItem: boolean;
  onButtonClick?: () => void;
  titleId?: string;
  currentProfile?: Profile | null;
  favoritesEnabled?: boolean;
  sidebar?: boolean;
};

const UserMenu = ({ showPaymentsItem, small = false, onButtonClick, currentProfile, favoritesEnabled, focusable, titleId, sidebar }: Props) => {
  const { t } = useTranslation('user');
  const navigate = useNavigate();
  const breakpoint = useBreakpoint();

  const accountController = getModule(AccountController);
  const user = useAccountStore((state) => state.user);
  const [logoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const tabIndex = focusable ? 0 : -1;
  const isMobile = breakpoint === Breakpoint.xs;

  const {
    isSwitchProfileEnable,
    isCognitoEnabled,
    isKatonCognitoEnabled,
    isSubscriptionEnabled,
    isAuthenticationEnabled,
    isGuestModeEnabled,
    guestStreamingEnabled,
    isQrAuthenticationEnabled,
    isUploadEnabled,
    isReelUploadEnabled,
    isVideoUploadEnabled,
    isModerateContentEnabled,
  } = useAppConfig();

  const hasAnyUploadEnabled = isReelUploadEnabled || isVideoUploadEnabled;

  const shouldShowProfileForGuestMode = !isAuthenticationEnabled && isGuestModeEnabled && guestStreamingEnabled && !isQrAuthenticationEnabled;

  const onLogout = useCallback(async () => {
    setLogoutConfirmationOpen(false);
    if (onButtonClick) {
      onButtonClick();
    }
    if (isCognitoEnabled || isKatonCognitoEnabled) {
      cognitoLogout();
    } else {
      await accountController.logout();
    }

    navigate('/', { replace: true });
  }, [onButtonClick, navigate, accountController, isCognitoEnabled, isKatonCognitoEnabled]);

  return (
    <>
      {/* <h2 className={styles.sectionHeader} id={titleId}>
        {t('nav.settings')}
      </h2> */}
      <ul className={styles.menuItems}>
        {isSwitchProfileEnable && currentProfile && (
          <>
            <li>
              <MenuButton
                small={small}
                onClick={onButtonClick}
                to={userProfileURL(currentProfile.id ?? '')}
                label={t('nav.profile')}
                startIcon={<ProfileCircle src={currentProfile?.avatar_url} alt={currentProfile?.name ?? ''} />}
              />
            </li>
            <li className={classNames(styles.divider, { [styles.small]: small })} />
          </>
        )}
        <li>
          <MenuButton
            small={small}
            onClick={onButtonClick}
            to={shouldShowProfileForGuestMode ? PATH_USER_SETTINGS : PATH_USER_ACCOUNT}
            label={shouldShowProfileForGuestMode ? t('nav.settings') : t('nav.account')}
            startIcon={!isMobile ?  <Icon icon={shouldShowProfileForGuestMode ? SettingsIcon : Account} /> : undefined}
            tabIndex={tabIndex}
          />
        </li>
        {/* {isFavoriteEnable && (
          <li>
            <MenuButton
              small={small}
              onClick={onButtonClick}
              to={RELATIVE_PATH_USER_FAVORITES}
              label={t('nav.favorites')}
              startIcon={<Icon icon={Favorite} />}
              tabIndex={tabIndex}
            />
          </li>
        )} */}
        {isSubscriptionEnabled && (
          <>
            <li className={classNames(styles.divider, { [styles.small]: small })} />
            <li>
              <MenuButton
                small={small}
                onClick={onButtonClick}
                to={PATH_USER_PAYMENTS}
                label={t('nav.payments')}
                startIcon={<Icon icon={BalanceWallet} />}
                tabIndex={tabIndex}
              />
            </li>
          </>
        )}
        {isUploadEnabled && hasAnyUploadEnabled && (
          <>
            <li className={classNames(styles.divider, { [styles.small]: small })} />
            <li>
              <MenuButton
                small={small}
                onClick={onButtonClick}
                to={PATH_USER_VIDEOS}
                label={t('nav.videos')}
                startIcon={!isMobile ? <Icon icon={VideoIcon} /> : undefined}
                tabIndex={tabIndex}
              />
            </li>
          </>
        )}
        {isModerateContentEnabled && user?.role === ROLES.SUPERADMIN && (
          <>
            <li className={classNames(styles.divider, { [styles.small]: small })} />
            <li>
              <MenuButton
                small={small}
                onClick={onButtonClick}
                to={PATH_USER_MODERATE}
                label={t('nav.moderate')}
                startIcon={<Icon icon={ModerateIcon} />}
                tabIndex={tabIndex}
              />
            </li>
          </>
        )}
        {!sidebar && !shouldShowProfileForGuestMode && (
          <li className={classNames(styles.divider, { [styles.small]: small })}>
            <MenuButton
              small={small}
              onClick={() => setLogoutConfirmationOpen(true)}
              label={t('nav.signout')}
              startIcon={<Icon icon={Exit} />}
              tabIndex={tabIndex}
            />
          </li>
        )}
      </ul>
      <ConfirmationDialog
        open={logoutConfirmationOpen}
        title={t('common:sign_out')}
        body={t('common:sign_out_content')}
        confirmLabel={t('common:yes')}
        closeLabel={t('common:no')}
        variant="delete"
        onConfirm={onLogout}
        onClose={() => setLogoutConfirmationOpen(false)}
      />
    </>
  );
};

export default UserMenu;
